import { ExternalLinkIcon, PlusIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProgressDots } from "@/components/ui/progress-dots";
import { cn } from "@/lib/utils";
import { formatCents } from "@/lib/format";

export type ActionBarVariant =
  | "idle"
  | "link-needed"
  | "add-missing"
  | "ready"
  | "unlinked-only";

interface ActionBarProps {
  variant: ActionBarVariant;
  totalCents: number;
  itemCount: number;
  secondaryLine: string;
  // Store the cart lives in ("Wong", "PlazaVea"), used in button copy.
  storeLabel: string;
  missingCount?: number;
  pending?: boolean;
  onPrimary: () => void;
  // Whether to hide on `lg+`. v2 keeps the bar visible on desktop until the
  // Recap card lands (parked for v1.1), so the default is now `false`.
  hideOnDesktop?: boolean;
}

// Variant-keyed background classes. `ready` replaces the base bg entirely
// (instead of stacking under the bg-background blur) so the emerald wash
// actually reads on screen.
const BAR_BG_BY_VARIANT: Record<Exclude<ActionBarVariant, "idle">, string> = {
  "link-needed": "bg-background/95 backdrop-blur-md border-border",
  "add-missing": "bg-background/95 backdrop-blur-md border-border",
  "unlinked-only": "bg-background/95 backdrop-blur-md border-border",
  ready:
    "bg-emerald-50/95 backdrop-blur-md border-emerald-200/70 dark:bg-emerald-950/60 dark:border-emerald-900",
};

export function ComprasActionBar({
  variant,
  totalCents,
  itemCount,
  secondaryLine,
  storeLabel,
  missingCount,
  pending = false,
  onPrimary,
  hideOnDesktop = false,
}: ActionBarProps) {
  if (variant === "idle") return null;

  const isReady = variant === "ready";
  const buttonAriaLabel = renderAriaLabel(variant, storeLabel, missingCount);
  const buttonVariant: "default" | "outline" = variant === "unlinked-only" ? "outline" : "default";

  return (
    <div
      role="region"
      aria-label="Acciones de carrito"
      // Sticky-bottom inside the scroll container so it sits above the existing
      // bottom day-bar nav. The design spec calls for `fixed`, but this app's
      // layout reserves the viewport bottom for the day-tab nav.
      className={cn(
        "sticky bottom-0 z-20 -mx-4 border-t px-4 safe-area-bottom motion-safe:transition-colors motion-safe:duration-300",
        BAR_BG_BY_VARIANT[variant],
        hideOnDesktop && "lg:hidden",
      )}
    >
      <div className="mx-auto flex max-w-2xl items-center gap-3 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold leading-none tabular-nums">
            {formatCents(totalCents)}
          </p>
          <p
            className={cn(
              "mt-1 truncate text-xs text-muted-foreground",
              isReady && "text-emerald-800 dark:text-emerald-200",
            )}
          >
            {secondaryLine}
          </p>
        </div>
        <Button
          type="button"
          size="lg"
          onClick={onPrimary}
          disabled={pending}
          variant={buttonVariant}
          aria-label={buttonAriaLabel}
          className={cn(
            "h-11 min-w-[140px] shrink-0 px-4",
            isReady && "bg-emerald-600 text-white hover:bg-emerald-700",
          )}
        >
          {pending ? <ProgressDots aria-label="Procesando" /> : renderPrimaryContent(variant, storeLabel, missingCount)}
        </Button>
      </div>
      <span aria-hidden="true" data-item-count={itemCount} className="hidden" />
    </div>
  );
}

function renderPrimaryContent(variant: ActionBarVariant, storeLabel: string, missingCount?: number) {
  switch (variant) {
    case "link-needed":
      return <span>Vincular productos</span>;
    case "add-missing":
      return (
        <>
          <PlusIcon className="size-4" />
          <span>Agregar {missingCount ?? 0}</span>
        </>
      );
    case "ready":
    case "unlinked-only":
      return (
        <>
          <ExternalLinkIcon className="size-4" />
          <span>Ir a {storeLabel}</span>
        </>
      );
    default:
      return null;
  }
}

function renderAriaLabel(variant: ActionBarVariant, storeLabel: string, missingCount?: number) {
  switch (variant) {
    case "link-needed":
      return "Vincular productos pendientes";
    case "add-missing": {
      const n = missingCount ?? 0;
      return `Agregar ${n} producto${n === 1 ? "" : "s"} faltante${n === 1 ? "" : "s"} al carrito de ${storeLabel}`;
    }
    case "ready":
      return `Abrir el carrito en ${storeLabel}`;
    case "unlinked-only":
      return `Abrir el carrito en ${storeLabel}`;
    default:
      return "";
  }
}
